"use client";

import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;
const COLORS = ["#5563F6", "#7782F8", "#99A1FA", "#BBC1FB"];

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function TopServicesStats() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/analytics/best-services/`, {
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        const formatted =
          data?.besd_services?.map((s) => ({
            name: s.name,
            percent: parseFloat(s.percentage) || 0,
          })) ?? [];

        setData(formatted);
        setClients(data?.clients || {});
      } catch (err) {
        console.error("❌ Failed to fetch Top Services Stats:", err);
        setError("Failed to load Top Services Stats.");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [token, navigate]);

  return (
    <div className="flex flex-col justify-start bg-white rounded-2xl shadow-md p-6 h-[343px] gap-[10px]" style={{paddingTop:"15px"}}>
      <h2 className="text-[#302B2B] text-2xl font-bold">
        Top Services Stats
      </h2>

      {loading ? (
        <p className="text-neutral-400 text-sm text-center">Loading...</p>
      ) : error ? (
        <p className="text-red-500 text-sm italic text-center">{error}</p>
      ) : data.length === 0 ? (
        <p className="text-[#9A99AB] text-sm text-center">
          No service data available
        </p>
      ) : (
        <div className="flex flex-row items-center gap-6">
          {/* ==== Donut Chart ==== */}
          <div className="w-[270px] h-[270px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="percent"
                  paddingAngle={2}
                  width={400}
                  height={400}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      stroke="none"
                    />
                  ))}
                </Pie>

                <Tooltip
                  cursor={false}
                  content={({ active, payload }) =>
                    active && payload && payload.length ? (
                      <div className="bg-white border border-[#F6F6F6] rounded-lg px-3 py-1 text-sm shadow">
                        <span className="text-[#302B2B] font-medium">
                          {payload[0].name}
                        </span>{" "}
                        <span className="text-[#5563F6]">
                          {payload[0].value}%
                        </span>
                      </div>
                    ) : null
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-col gap-5">
            {data.map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 w-[205px]"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-[12px] h-[4px] rounded-md"
                    style={{ background: COLORS[i % COLORS.length] }}
                  ></div>
                  <span className="text-[#302B2B] text-[18px] font-normal">
                    {item.name}
                  </span>
                </div>
                <span className="text-[#9A99AB] text-[16px]">
                  {item.percent}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}